import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "react-hot-toast";

const RSVPForm = () => {
  const [formData, setFormData] = useState({
    titleid: "1",
    fullname: "",
    countryCode: "+44",
    phoneNo: "",
    email: "",
    companyName: "",
    reCaptcha: "",
  });

  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();

  // Live field validation for submit button state
  useEffect(() => {
    const nameRegex = /^[A-Za-z\s]+$/;
    const phoneRegex = /^[0-9]{8,15}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const zeroCount = (formData.phoneNo.match(/0/g) || []).length;

    const isValid =
      formData.titleid &&
      formData.fullname &&
      nameRegex.test(formData.fullname) &&
      formData.phoneNo &&
      phoneRegex.test(formData.phoneNo) &&
      zeroCount <= 3 &&
      formData.email &&
      emailRegex.test(formData.email) &&
      formData.companyName;

    setIsFormValid(isValid);
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    const nameRegex = /^[A-Za-z\s]+$/;
    const phoneRegex = /^[0-9]{8,15}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.titleid) newErrors.titleid = "Title is required";

    if (!formData.fullname) {
      newErrors.fullname = "Full Name is required.";
    } else if (!nameRegex.test(formData.fullname)) {
      newErrors.fullname = "Full Name should contain only letters and spaces.";
    }

    if (!formData.phoneNo) {
      newErrors.phoneNo = "Phone No is required.";
    } else if (!phoneRegex.test(formData.phoneNo)) {
      newErrors.phoneNo = "Phone No must be 8–15 digits and numbers only.";
    } else if ((formData.phoneNo.match(/0/g) || []).length > 3) {
      newErrors.phoneNo = "Phone No cannot contain more than three zeros.";
    }

    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.companyName)
      newErrors.companyName = "Company Name is required.";

    // if (!formData.reCaptcha)
    //   newErrors.reCaptcha = "Please verify that you are not a robot.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setSubmitMessage({
        type: "error",
        text: "Please fill all the form fields correctly.",
      });
      return false;
    }

    return true;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNameKeyPress = (e) => {
    if (!/^[A-Za-z\s]*$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePhoneKeyPress = (e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleReCaptcha = (value) => {
    setFormData((prev) => ({ ...prev, reCaptcha: value }));
    setErrors((prev) => ({ ...prev, reCaptcha: "" }));
  };

  const trimFormData = (data) => {
    const trimmedData = {};
    Object.keys(data).forEach((key) => {
      const value = data[key];
      trimmedData[key] = typeof value === "string" ? value.trim() : value;
    });
    return trimmedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateForm()) return;

    window.grecaptcha?.reset();
    setIsSubmitting(true);

    let toastId;
    try {
      toastId = toast.loading(<b>Saving...</b>);
      const trimmedFormData = trimFormData(formData);

      // 1. Prepare secondary payload and configuration
      const secondaryPayload = {
        name: trimmedFormData.fullname,
        email: trimmedFormData.email,
        company: trimmedFormData.companyName,
        attendeeType: "attendee",
      };

      const secretKey =
        process.env.REACT_APP_MSB_REGISTRATION_SECRET ||
        (typeof import.meta !== "undefined" && import.meta.env ? import.meta.env.VITE_MSB_REGISTRATION_SECRET : null) ||
        "msb_association_secret_2026";

      // 2. Fire both API calls in parallel
      const primaryPromise = axios.post(
        "https://msbassociationuk.org/prod/api/msb/saversvpregistration",
        trimmedFormData
      );

      const secondaryPromise = axios.post(
        "https://msb-registration-portal.vercel.app/api/register",
        secondaryPayload,
        {
          headers: {
            "x-msb-registration-secret": secretKey,
          },
        }
      ).catch(secondaryError => {
        // Log secondary failure but don't let it throw and block the primary flow
        console.error("Secondary registration failed:", {
          error: secondaryError.message,
          response: secondaryError.response?.data,
          status: secondaryError.response?.status,
        });
        return { data: { success: false, error: "Secondary API failed" } };
      });

      // 3. Wait for both calls
      const [primaryResponse] = await Promise.all([
        primaryPromise,
        secondaryPromise
      ]);

      // 4. Handle results (Primary success is the main criteria)
      if (primaryResponse.data.response && primaryResponse.data.responseCode === "00") {
        toast.dismiss(toastId);
        toast.success(<b>Form submitted successfully!</b>);
        setSubmitMessage({
          type: "success",
          text: "Form submitted successfully!",
        });

        setTimeout(() => {
          navigate("/welcome-meet");
        }, 1000);

        setFormData({
          titleid: "1",
          fullname: "",
          countryCode: "+44",
          phoneNo: "",
          email: "",
          companyName: "",
          reCaptcha: "",
        });
        setErrors({});
      } else {
        toast.dismiss(toastId);
        const msg = primaryResponse.data.data || "Something went wrong. Please try again.";
        toast.error(msg);
        setSubmitMessage({ type: "error", text: msg });
      }
    } catch (error) {
      toast.dismiss(toastId);
      const msg =
        error.response?.data?.data || "Something went wrong. Please try again.";
      toast.error(msg);
      setSubmitMessage({ type: "error", text: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (submitMessage) {
      const timer = setTimeout(() => setSubmitMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [submitMessage]);

  return (
    <div className="meet-details-box">
      <h5 className="mb-3">RSVP Form</h5>
      <p className="heading_description mb-2">
        Please fill out the form below to register for the MSB Association Members Meet.
      </p>

      {submitMessage && (
        <div
          className={`alert ${submitMessage.type === "success" ? "alert-success" : "alert-danger"
            }`}
        >
          {submitMessage.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row mt-2">
          {/* Title */}
          <div className="col-md-2 col-12">
            <div className="form-group">
              <label className="input_title mt-3" htmlFor="titleid">
                Title<sup>*</sup>
              </label>
              <select
                id="titleid"
                className={`form-control select-arrow ${errors.titleid ? "bg-danger text-white" : ""}`}
                name="titleid"
                value={formData.titleid}
                onChange={handleInputChange}
              >
                {[{ label: "Mr.", value: "1" }, { label: "Mrs.", value: "2" }, { label: "Miss", value: "3" }, { label: "Dr.", value: "4" }].map(
                  (title) => (
                    <option key={title.value} value={title.value}>
                      {title.label}
                    </option>
                  )
                )}
              </select>
              {errors.titleid && <span className="text-danger">{errors.titleid}</span>}
            </div>
          </div>

          {/* Full Name */}
          <div className="col-md-10">
            <div className="form-group">
              <label className="input_title mt-3" htmlFor="fullname">
                Full Name<sup>*</sup>
              </label>
              <input
                id="fullname"
                className={`form-control ${errors.fullname ? "bg-danger" : ""}`}
                type="text"
                name="fullname"
                value={formData.fullname}
                onChange={handleInputChange}
                onKeyPress={handleNameKeyPress}
              />
              {errors.fullname && <span className="text-danger">{errors.fullname}</span>}
            </div>
          </div>

          {/* Phone */}
          <div className="col-md-6 mt-3">
            <div className="form-group">
              <label className="input_title" htmlFor="phoneNo">
                Phone<sup>*</sup>
              </label>
              <div className="input-group">
                <select
                  id="countryCode"
                  className="form-control select-arrow p-3"
                  name="countryCode"
                  style={{ maxWidth: "80px" }}
                  value={formData.countryCode}
                  onChange={handleInputChange}
                >
                  <option value="+44">+44</option>
                </select>
                <input
                  className={`form-control ${errors.phoneNo ? "bg-danger" : ""}`}
                  type="text"
                  name="phoneNo"
                  value={formData.phoneNo}
                  onChange={handleInputChange}
                  onKeyPress={handlePhoneKeyPress}
                />
              </div>
              {errors.phoneNo && <span className="text-danger">{errors.phoneNo}</span>}
            </div>
          </div>

          {/* Email */}
          <div className="col-md-6 mt-3">
            <div className="form-group">
              <label className="input_title" htmlFor="email">
                Email<sup>*</sup>
              </label>
              <input
                id="email"
                className={`form-control ${errors.email ? "bg-danger" : ""}`}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
              {errors.email && <span className="text-danger">{errors.email}</span>}
            </div>
          </div>

          {/* Company Name */}
          <div className="col-md-12 mt-3">
            <div className="form-group">
              <label className="input_title" htmlFor="companyName">
                Company Name<sup>*</sup>
              </label>
              <input
                id="companyName"
                className={`form-control ${errors.companyName ? "bg-danger" : ""}`}
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
              />
              {errors.companyName && <span className="text-danger">{errors.companyName}</span>}
            </div>
          </div>

          {/* reCAPTCHA */}
          <div className="col-md-12 mt-3">
            <ReCAPTCHA
              sitekey="6LfmsZoqAAAAAPQgbaeEuX7w8IZxz7Ncqgs2i0hY"
              onChange={handleReCaptcha}

            />
            {errors.reCaptcha && <span className="text-danger">{errors.reCaptcha}</span>}
          </div>

          {/* Submit Button */}
          <div className="col-md-12">
            <div className="text-center mt-5 mb-3">
              <button
                type="submit"
                className="btn btn-primary btn-lg w-100"
              // disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RSVPForm;
